// Generic capture form for one specialty add-on — docs/HMS_V4_IP_CASE_SHEET_
// INDIA_DESIGN.md §8, Phase 8. Driven by specialtyRecordFields.ts's group/
// field spec rather than three hand-written forms, since the three
// templates differ only in which fields exist, not in how a field behaves.
//
// Path addressing: a field's `key` is a dotted path WITHIN its group's own
// object in `data` (e.g. group "obstetric", field "gpla.g" → data.obstetric.gpla.g).
// A field whose own `key` is `''` means the group's entire value IS that one
// field (e.g. group "contraception_counselling" is itself a string, not an
// object with a sub-key) — see specialtyRecordFields.ts's own comment on
// the two scalar-top-level-key groups this applies to.
import { Input, Textarea } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Button } from '@/components/ui/Button';
import VoiceFormFiller, { type VoiceField } from '@/components/VoiceFormFiller';
import type { SpecialtyGroupSpec, SpecialtyFieldSpec } from './specialtyRecordFields';
import { appendText, vBool, vNum, vText, type Extracted } from '@/lib/voiceFill';

// Voice keys address a field as "<group>|<dotted field path>". List fields
// (implants, transfusions, immunisations, previous deliveries) are left out:
// a repeatable row is added with its own button, and splitting one spoken
// passage into N rows is exactly the kind of guess this form should not make.
const VOICE_SEP = '|';
function voiceFieldsFor(groups: SpecialtyGroupSpec[]): VoiceField[] {
  const out: VoiceField[] = [];
  for (const g of groups) {
    for (const f of g.fields) {
      if (f.kind === 'list') continue;
      const label = `${g.label} — ${f.label}${f.suffix ? ` (${f.suffix})` : ''}`;
      const key = `${g.key}${VOICE_SEP}${f.key}`;
      if (f.kind === 'boolean') out.push({ key, label, type: 'radio', options: ['yes', 'no'] });
      else out.push({ key, label, type: f.kind === 'number' ? 'number' : 'text' });
    }
  }
  return out;
}

function getPath(obj: unknown, path: string): unknown {
  if (path === '') return obj;
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);
}

function setPath(obj: Record<string, unknown>, path: string, value: unknown): Record<string, unknown> {
  if (path === '') return value as Record<string, unknown>;
  const parts = path.split('.');
  const next: Record<string, unknown> = { ...obj };
  let cursor = next;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i] ?? '';
    const existing = cursor[p];
    const copy = existing && typeof existing === 'object' ? { ...(existing as Record<string, unknown>) } : {};
    cursor[p] = copy;
    cursor = copy;
  }
  cursor[parts[parts.length - 1] ?? ''] = value;
  return next;
}

/**
 * Phase 10's list-field repeater: implants[]/transfusion[]/immunisation[]
 * (a top-level group whose entire value IS the array, field.key === '') and
 * previous_deliveries[] (nested inside the `obstetric` group alongside its
 * other scalar fields). Either shape reaches here the same way — `value` is
 * already the array at this field's path, resolved by the same
 * getPath()/setPath() every other field uses, so this component only needs
 * to know how to grow/shrink/edit that array; addressing is unchanged.
 */
function ListFieldInput({ field, value, onChange, readOnly }: {
  field: SpecialtyFieldSpec; value: unknown; onChange: (v: unknown[]) => void; readOnly: boolean;
}) {
  const items = Array.isArray(value) ? value : [];
  const itemFields = field.itemFields ?? [];
  const itemLabel = field.itemLabel || 'item';

  const updateItem = (index: number, itemKey: string, itemValue: unknown) => {
    onChange(items.map((it, i) => (i === index ? { ...(it as Record<string, unknown>), [itemKey]: itemValue } : it)));
  };
  const addItem = () => onChange([...items, {}]);
  const removeItem = (index: number) => onChange(items.filter((_, i) => i !== index));

  return (
    <div className="sm:col-span-2">
      <div className="mb-1 text-[12px] font-medium text-ink">{field.label}</div>
      {!items.length && <div className="text-[12px] text-ink-muted">None recorded.</div>}
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="rounded-[var(--radius)] border border-border p-2">
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {itemFields.map((itemField) => (
                <FieldInput
                  key={itemField.key}
                  field={itemField}
                  value={(item as Record<string, unknown> | null)?.[itemField.key]}
                  readOnly={readOnly}
                  onChange={(v) => updateItem(i, itemField.key, v)}
                />
              ))}
            </div>
            {!readOnly && (
              <Button type="button" size="sm" variant="ghost" className="mt-2" onClick={() => removeItem(i)}>
                Remove this {itemLabel}
              </Button>
            )}
          </div>
        ))}
      </div>
      {!readOnly && (
        <Button type="button" size="sm" variant="ghost" className="mt-2" onClick={addItem}>
          + Add {itemLabel}
        </Button>
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange, readOnly }: {
  field: SpecialtyFieldSpec; value: unknown; onChange: (v: unknown) => void; readOnly: boolean;
}) {
  if (field.kind === 'list') {
    return <ListFieldInput field={field} value={value} onChange={onChange as (v: unknown[]) => void} readOnly={readOnly} />;
  }
  if (field.kind === 'boolean') {
    return (
      <label className="flex items-center gap-2 text-[13px] text-ink">
        <Checkbox checked={value === true} disabled={readOnly} onCheckedChange={(c) => onChange(c === true)} />
        {field.label}
      </label>
    );
  }
  if (field.kind === 'textarea') {
    return (
      <div>
        <label className="mb-1 block text-[12px] font-medium text-ink">{field.label}</label>
        <Textarea
          value={typeof value === 'string' ? value : ''} disabled={readOnly} rows={3}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }
  const inputType = field.kind === 'number' ? 'number' : 'text';
  return (
    <div>
      <label className="mb-1 block text-[12px] font-medium text-ink">
        {field.label}{field.suffix ? ` (${field.suffix})` : ''}
      </label>
      <Input
        type={inputType}
        value={value === null || value === undefined ? '' : String(value)}
        disabled={readOnly}
        onChange={(e) => onChange(field.kind === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value)}
      />
    </div>
  );
}

export function SpecialtyRecordForm({ groups, data, onChange, readOnly }: {
  groups: SpecialtyGroupSpec[];
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly: boolean;
}) {
  const setField = (group: SpecialtyGroupSpec, field: SpecialtyFieldSpec, value: unknown) => {
    const groupValue = data[group.key];
    const nextGroupValue = field.key === ''
      ? value
      : setPath((groupValue && typeof groupValue === 'object' ? groupValue as Record<string, unknown> : {}), field.key, value);
    onChange({ ...data, [group.key]: nextGroupValue });
  };

  // Every answer is applied onto one copy of `data` and handed up once —
  // calling setField per answer would have each one overwrite the last,
  // since each reads the same pre-dictation `data`.
  const applyVoice = (x: Extracted) => {
    let next = { ...data };
    for (const group of groups) {
      for (const field of group.fields) {
        if (field.kind === 'list') continue;
        const key = `${group.key}${VOICE_SEP}${field.key}`;
        const current = getPath(next[group.key], field.key);
        let value: unknown;
        if (field.kind === 'boolean') value = vBool(x, key);
        else if (field.kind === 'number') value = vNum(x, key);
        else {
          const t = vText(x, key);
          value = t === undefined ? undefined : appendText(typeof current === 'string' ? current : '', t, '; ');
        }
        if (value === undefined) continue;
        const groupValue = next[group.key];
        next = {
          ...next,
          [group.key]: field.key === ''
            ? value
            : setPath((groupValue && typeof groupValue === 'object' ? groupValue as Record<string, unknown> : {}), field.key, value),
        };
      }
    }
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {!readOnly && (
        <VoiceFormFiller fields={voiceFieldsFor(groups)} onFields={applyVoice} label="🎙 Dictate this record"
          hint="Name each item as you say it — the matching boxes below fill in for you to check" />
      )}
      {groups.map((group) => (
        <div key={group.key} className="rounded-[var(--radius)] border border-border p-3">
          <div className="mb-2 text-[12px] font-semibold text-ink">{group.label}</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {group.fields.map((field) => (
              <FieldInput
                key={field.key}
                field={field}
                value={getPath(data[group.key], field.key)}
                readOnly={readOnly}
                onChange={(v) => setField(group, field, v)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
